/**
 * TipsSuccessPage · iter137
 * Bestätigungsseite nach Stripe-Checkout-Rückkehr eines Tips.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Heart, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function TipsSuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tipId = params.get("tip_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!tipId) { setStatus("unknown"); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get("/tips/sent", { params: { limit: 200 } });
        const tip = (data?.tips || []).find((t) => t.tip_id === tipId);
        if (tip?.status === "PAID") { if (!cancelled) setStatus("paid"); return; }
      } catch (e) { /* ignore */ }
      tries += 1;
      if (tries < 6 && !cancelled) setTimeout(poll, 2000);
      else if (!cancelled) setStatus("pending");
    };
    poll();
    return () => { cancelled = true; };
  }, [tipId]);

  return (
    <div data-testid="tips-success-page" className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-pink-50 border border-pink-200 mb-6">
          {status === "paid" ? (
            <CheckCircle2 size={36} className="text-emerald-600" data-testid="tip-success-paid" />
          ) : (
            <Heart size={36} className="text-pink-600 fill-pink-600 animate-pulse" />
          )}
        </div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] mb-2">
          {status === "paid"
            ? t("tip_success.title_paid", { defaultValue: "Danke fürs Trinkgeld!" })
            : t("tip_success.title_pending", { defaultValue: "Zahlung wird verarbeitet …" })}
        </h1>
        <p className="text-sm text-[#666] mb-8">
          {status === "paid"
            ? t("tip_success.body_paid", { defaultValue: "Der Creator hat eine Benachrichtigung erhalten. Danke, dass du seine Arbeit unterstützt!" })
            : t("tip_success.body_pending", { defaultValue: "Wir warten auf Bestätigung von Stripe. Das dauert normalerweise wenige Sekunden." })}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button
            data-testid="tip-success-back"
            onClick={() => navigate(-1)}
            className="h-11 px-5 rounded-full bg-[#F5F5F5] text-[#0A0A0A] text-sm font-medium hover:bg-[#EAEAEA] flex items-center justify-center gap-2">
            <ArrowLeft size={16} /> {t("tip_success.back", { defaultValue: "Zurück" })}
          </button>
          <button
            data-testid="tip-success-home"
            onClick={() => navigate("/")}
            className="h-11 px-5 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#222]">
            {t("tip_success.home", { defaultValue: "Zur Startseite" })}
          </button>
        </div>
      </div>
    </div>
  );
}
